import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";

const PLANS = [
  {
    key: "free", name: "Free", tagline: "Try the magic",
    monthly: 0, yearly: 0,
    features: ["1 website", "AI generation & editor", "MapLift subdomain", "Mobile responsive"],
    lookup: null,
  },
  {
    key: "pro", name: "Pro", tagline: "For growing businesses", highlight: true,
    monthly: 19, yearly: 182,
    features: ["Everything in Free", "5 websites", "Custom domain", "SEO health dashboard", "AI Growth suggestions", "Remove MapLift badge"],
    lookup: { month: "pro_monthly", year: "pro_yearly" },
  },
  {
    key: "agency", name: "Agency", tagline: "For agencies & multi-location",
    monthly: 49, yearly: 470,
    features: ["Everything in Pro", "Unlimited websites", "Bulk import", "Priority AI generation", "White-label", "Client management"],
    lookup: { month: "agency_monthly", year: "agency_yearly" },
  },
];

export default function Pricing() {
  const [cycle, setCycle] = useState("month");
  const [loading, setLoading] = useState(null);
  const { user } = useAuth();
  const nav = useNavigate();

  const checkout = async (plan) => {
    if (!plan.lookup) { nav(user ? "/dashboard" : "/register"); return; }
    if (!user) { nav(`/register?next=${encodeURIComponent("/pricing")}`); return; }
    setLoading(plan.key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key: plan.lookup[cycle],
        origin_url: window.location.origin,
        user_id: user.id,
      });
      // Stripe Checkout cannot render inside an iframe (e.g. the preview). Break out:
      // navigate the TOP window if same-origin-accessible, otherwise open a new tab.
      const inIframe = window.self !== window.top;
      if (inIframe) {
        let topNavigated = false;
        try {
          window.top.location.href = data.checkout_url;
          topNavigated = true;
        } catch (_) { /* cross-origin top — cannot navigate it */ }
        if (!topNavigated) {
          const win = window.open(data.checkout_url, "_blank", "noopener");
          if (!win) {
            toast.error("Please allow pop-ups to continue to secure checkout.");
          }
        }
      } else {
        window.location.href = data.checkout_url;
      }
      setLoading(null);
    } catch (e) { toast.error(apiError(e)); setLoading(null); }
  };

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <div className="max-w-6xl mx-auto px-6 sm:px-10 py-16">
        <div className="text-center">
          <div className="ml-label mb-3">Pricing</div>
          <h1 className="font-heading font-bold text-4xl sm:text-5xl tracking-tighter text-[#09090b]">Simple pricing that scales with you.</h1>
          <p className="font-body text-lg text-[#52525b] mt-4">Start free. Upgrade when you're ready to grow.</p>

          <div className="inline-flex items-center gap-1 bg-[#f4f4f5] rounded-full p-1 mt-8">
            {["month", "year"].map((c) => (
              <button key={c} data-testid={`cycle-${c}`} onClick={() => setCycle(c)}
                className={`px-5 py-2 rounded-full text-sm font-body font-semibold ${cycle === c ? "bg-white shadow-sm text-[#09090b]" : "text-[#52525b]"}`}>
                {c === "month" ? "Monthly" : "Yearly"} {c === "year" && <span className="ml-ai-text">−20%</span>}
              </button>
            ))}
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-6 mt-14 items-start">
          {PLANS.map((p) => (
            <div key={p.key} data-testid={`plan-${p.key}`}
              className={`rounded-2xl p-8 ${p.highlight ? "bg-[#09090b] text-white ring-2 ring-sky-500" : "bg-white border border-border"}`}>
              {p.highlight && <div className="ml-label text-sky-400 mb-2">Most popular</div>}
              <h3 className={`font-heading font-bold text-2xl ${p.highlight ? "text-white" : "text-[#09090b]"}`}>{p.name}</h3>
              <p className={`font-body text-sm mt-1 ${p.highlight ? "text-white/60" : "text-[#52525b]"}`}>{p.tagline}</p>
              <div className="mt-6 flex items-end gap-1">
                <span className={`font-heading font-bold text-4xl ${p.highlight ? "text-white" : "text-[#09090b]"}`}>${cycle === "month" ? p.monthly : p.yearly}</span>
                <span className={`font-body text-sm mb-1 ${p.highlight ? "text-white/50" : "text-[#a1a1aa]"}`}>/{cycle}</span>
              </div>
              <button data-testid={`select-${p.key}`} onClick={() => checkout(p)} disabled={loading === p.key}
                className={`w-full mt-6 rounded-full py-3 font-semibold text-sm font-body inline-flex items-center justify-center gap-2 ${p.highlight ? "bg-white text-[#09090b]" : "bg-[#09090b] text-white"}`}>
                {loading === p.key ? <Loader2 size={16} className="animate-spin" /> : (p.lookup ? "Upgrade" : "Get started")}
              </button>
              <div className="mt-7 space-y-3">
                {p.features.map((f) => (
                  <div key={f} className="flex items-center gap-2.5">
                    <Check size={16} className={p.highlight ? "text-sky-400" : "text-emerald-500"} />
                    <span className={`font-body text-sm ${p.highlight ? "text-white/80" : "text-[#52525b]"}`}>{f}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
        <p className="text-center font-body text-xs text-[#a1a1aa] mt-8">Test mode · use card 4242 4242 4242 4242 with any future expiry and CVC.</p>
      </div>
      <Footer />
    </div>
  );
}
